import { ReactNode, useState } from 'react';

import { CoreBox, CoreDialog, CoreDialogContent, CoreDialogTitle, CoreTypography } from '@youscience/core';
import type { SocCodeInfo } from '@common-types';

import ActionButton from '@components/ActionButton';
import useUserInfoStore from '@store/UserInfoStore';
import useFavoriteCareersStore from '@store/FavoriteCareersStore';

import { EllipsisTypography } from '@components/EllipsisTypography';

import { OVERALL_FIT_DATA } from '@constants/career';
import { DISCOVERY_APPS_URL } from '@constants/externalRoutes';

import HeartIcon from '@images/heart.svg?react';
import HatIcon from '@images/hat.svg?react';
import ArrowIcon from '@images/arrow.svg?react';
import SalaryIcon from '@images/salary.svg?react';
import FilledHeartIcon from '@images/heart-filled.svg?react';
import ChevronRight from '@images/chevron-right.svg?react';
import defaultCareerPhoto from '@images/default-career-photo.png';

import DialogFooter from '@components/DialogFooter';
import {
  sxStyles,
  StyledCareerCard,
  StyledCareerPhoto,
  StyledOverallFitWrap,
  StyledCareerDetails,
  StyledActionsContainer,
  StyledMoreLink,
  StyledMoreButton,
  StyledLink,
} from './CareerCard.styles';
import useAuthStore from '@store/AuthStore';
import useHasTakenBrainGames from '@hooks/useHasTakenBrainGames';

export const renderInfographicItem = (icon: ReactNode, title: string, value?: string) => {
  return (
    <CoreBox sx={sxStyles.infographicItem}>
      <CoreTypography sx={sxStyles.infographicTitle} component="span" variant="body2">
        {title}
      </CoreTypography>

      {value ? (
        <CoreBox sx={sxStyles.infographicValue}>
          {icon}

          <CoreTypography component="span" variant="caption">
            {value}
          </CoreTypography>
        </CoreBox>
      ) : (
        '–'
      )}
    </CoreBox>
  );
};

interface CareerCardProps {
  career: SocCodeInfo;
  componentId?: string;
}

export const CareerCard = (props: CareerCardProps) => {
  const {
    componentId,
    career: {
      socCode,
      images: [careerImg],
      title,
      educationInvestment = '',
      projectOpenings = '',
      averageSalary = '',
      ranked_overall_fit,
    },
  } = props;

  const [isOpenBrainGamesMessage, setIsOpenBrainGamesMessage] = useState(false);

  const {
    userInfo: { userId },
  } = useUserInfoStore.getState();

  const hasTakenBrainGames = useHasTakenBrainGames();

  const favoriteCareersStore = useFavoriteCareersStore((state) => state.favoriteCareers);
  const isFavoriteCareersLoading = useFavoriteCareersStore((state) => state.isLoading);
  const toggleFavoriteCareer = useFavoriteCareersStore((state) => state.toggleFavoriteCareer);

  const addToFavoritesButtonId = componentId ? `${componentId}-add-to-favorites-button` : 'add-to-favorites-button';
  const careerView = careerImg.url ? careerImg.url : defaultCareerPhoto;

  const handleAddToFavorites = () => {
    void toggleFavoriteCareer(socCode, userId);
  };

  const handleShowBrainGamesModal = () => {
    setIsOpenBrainGamesMessage(true);
  };

  const handleGoToBrainGames = () => {
    setIsOpenBrainGamesMessage(false);
    window.open(`${DISCOVERY_APPS_URL}/home`, '_blank');
  };

  return (
    <>
      <StyledCareerCard>
        <StyledCareerPhoto>
          <img src={careerView} alt="career view" />
        </StyledCareerPhoto>

        <StyledOverallFitWrap>
          {ranked_overall_fit > 0 && OVERALL_FIT_DATA[ranked_overall_fit] ? (
            <>
              <CoreBox data-testid="rankedFitIcon">{OVERALL_FIT_DATA[ranked_overall_fit].icon}</CoreBox>

              <CoreTypography data-testid="rankedFitTitle" sx={sxStyles.overallFitTitle} variant="caption">
                {OVERALL_FIT_DATA[ranked_overall_fit].title}
              </CoreTypography>
            </>
          ) : null}
        </StyledOverallFitWrap>

        <StyledCareerDetails>
          <EllipsisTypography stringCount={2} variant="h5" component="h4">
            {title}
          </EllipsisTypography>

          <CoreBox sx={sxStyles.infographicsWrap}>
            {renderInfographicItem(<HatIcon />, 'Edu. investment', educationInvestment)}

            {renderInfographicItem(<ArrowIcon />, 'Proj. openings', projectOpenings)}

            {renderInfographicItem(<SalaryIcon />, 'Avg. salary', averageSalary)}
          </CoreBox>
        </StyledCareerDetails>

        <StyledActionsContainer data-testid="actionsContainer">
          <ActionButton
            data-testid="addToFavoritesButton"
            id={addToFavoritesButtonId}
            sx={sxStyles.favoriteBtn}
            variant="text"
            type="button"
            disabled={isFavoriteCareersLoading}
            onClick={hasTakenBrainGames ? handleAddToFavorites : handleShowBrainGamesModal}
          >
            {favoriteCareersStore[socCode] ? (
              <FilledHeartIcon data-testid="filled" />
            ) : (
              <HeartIcon data-testid="notFilled" />
            )}
          </ActionButton>

          {hasTakenBrainGames ? (
            <StyledMoreLink
              data-testid="moreCareerInfoLink"
              href={`${DISCOVERY_APPS_URL}/careers#/${socCode}`}
              target="_blank"
              rel="noreferrer"
            >
              More
              <ChevronRight />
            </StyledMoreLink>
          ) : (
            <StyledMoreButton data-testid="moreCareerInfoButton" onClick={handleShowBrainGamesModal}>
              More
              <ChevronRight />
            </StyledMoreButton>
          )}
        </StyledActionsContainer>
      </StyledCareerCard>

      <CoreDialog data-testid="brainGamesDialog" sx={sxStyles.dialogWrap} open={isOpenBrainGamesMessage}>
        <CoreDialogTitle sx={sxStyles.title}>
          <CoreTypography variant="h5" component="div">
            Unlock your superpower!
          </CoreTypography>
        </CoreDialogTitle>

        <CoreDialogContent sx={sxStyles.content}>
          Unlock your superpower with{' '}
          <StyledLink to={`${DISCOVERY_APPS_URL}/home`} target="_blank" rel="noopener noreferrer">
            Brain games
          </StyledLink>{' '}
          to discover your aptitudes, interests, and matching best-fit careers and educational opportunities.
        </CoreDialogContent>

        <DialogFooter
          goBackButtonTitle="I'll do it later"
          goNextButtonTitle="Go to Brain Games"
          onGoBack={() => setIsOpenBrainGamesMessage(false)}
          onGoNext={handleGoToBrainGames}
        />
      </CoreDialog>
    </>
  );
};
